'use client'

import { memo, useCallback } from 'react'
import {
  Ninxl8BitDivider,
  Ninxl8BitSection,
  Ninxl8BitThemeProvider,
} from '@/app/(marketing)/_components/8bit'
import '../../ninxl/craft.css'
import '../nin-clean.css'
import '../nin-craft.css'
import { CRAFT_MEDIA } from '@/app/ninxl/_components/craft-media'
import { NinCraftNav } from '../_components/nin-craft-nav'
import { NinCraftHero } from '../_components/nin-craft-hero'
import { NinOrgFooter } from '../_components/nin-org-footer'
import { NinScrollWorld } from '../_components/scroll-world/nin-scroll-world'
import { NinPostHeroStart } from '../_components/nin-post-hero-start'
import { NinOrgDemoSection } from '../_components/nin-org-demo-section'
import { NinProductGrid } from '../_components/nin-product-grid'
import { NinPrivacyVault } from '../_components/nin-privacy-vault'
import { NinContactSection } from '@/app/(marketing)/_components/nin-contact-section'
import { NinConnectPlatformSection } from '@/app/(marketing)/_components/nin-connect-platform-section'
import {
  ExpandingFeatureCards,
  type ExpandingFeatureCard,
} from '@/app/(marketing)/_components/expanding-feature-cards'
import {
  MarketingCardMedia,
  NIN_CARD_MEDIA,
} from '@/app/(marketing)/_components/marketing-card-media'
import { MarketingContentMarquee } from '@/app/(marketing)/_components/marketing-content-marquee'
import { CmsLandingHighlights } from '@/app/(marketing)/_components/cms/cms-landing-highlights'
import { NinxlScrollReveal } from '@/app/ninxl/_components/ninxl-scroll-reveal'
import {
  Laptop,
  Mic,
  Smartphone,
  Shield,
  Lock,
  Sparkles,
} from 'lucide-react'

const NIN_CONTENT_TILES = [
  { id: 'desktop', title: 'NINX', subtitle: 'Private desktop AI', icon: Laptop },
  { id: 'tray', title: 'NINK', subtitle: 'Tray voice writing', icon: Mic },
  { id: 'mobile', title: 'Ningenie', subtitle: 'Mobile agents', icon: Smartphone },
  { id: 'privacy', title: 'Privacy', subtitle: 'Your data stays yours', icon: Shield },
  { id: 'local', title: 'On device', subtitle: 'Local by design', icon: Lock },
  { id: 'agents', title: 'Agents', subtitle: 'Under your approval', icon: Sparkles },
] as const

const NIN_EXPAND_CARDS: ExpandingFeatureCard[] = [
  {
    id: 'ninxl',
    title: 'NINX',
    tagline: 'Private desktop AI',
    description:
      'Local-first agent for Mac and PC: chat, voice, tools, and automations that stay on your machine by default.',
    href: '/ninxl',
    ctaLabel: 'Get NINX',
  },
  {
    id: 'nink',
    title: 'NINK',
    tagline: 'Tray voice writing',
    description:
      'Ink Flow from the menu bar. Speak into any app. Whisper and rewrite stay local.',
    href: '/nink',
    ctaLabel: 'Get NINK',
  },
  {
    id: 'ningenie',
    title: 'Ningenie',
    tagline: 'Mobile agents',
    description:
      "Don't ask, just task: a digital assistant that executes across your phone apps.",
    href: '/home',
    ctaLabel: 'Open Ningenie',
  },
]

const NinOrgPageContent = memo(function NinOrgPageContent() {
  const scrollTo = useCallback((id: string) => {
    const target =
      document.getElementById(id) ??
      document.querySelector<HTMLElement>(`[data-section="${id}"]`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    // baby: craft kraft shell like /ninxl: only Navigating India pong stays 8-bit
    <div
      className="nin-landing nin-clean ninxl-landing ninxl-8bit ninxl-craft min-h-screen overflow-x-hidden"
      data-ninxl-theme="light"
      data-testid="nin-clean-shell"
    >
      <div className="ninxl-craft__content">
        {/* baby: Nav stays page-sticky; kraft atmosphere only wraps the opening hero. */}
        <NinCraftNav onScrollTo={scrollTo} />
        <section className="nin-craft-first" aria-label="NIN opening">
          <video
            className="nin-craft-first__atmosphere"
            src={CRAFT_MEDIA.atmosphereVideo}
            muted
            playsInline
            autoPlay
            loop
            preload="none"
            aria-hidden
          />
          <div className="nin-craft-first__edge" aria-hidden />
          <div className="nin-craft-first__grain" aria-hidden />
          <div className="nin-craft-first__content">
            <NinCraftHero />
          </div>
        </section>

        {/* HERO: scroll-scrubbed fly-through the NIN sanctuary */}
        <NinScrollWorld />

        <NinPostHeroStart />
        <NinOrgDemoSection />

        <Ninxl8BitDivider />

        {/* EXPAND: product pillars (craft SF Pro via ninxl-craft overrides) */}
        <Ninxl8BitSection alt data-section="expand" className="relative z-20">
          <ExpandingFeatureCards
            label="GUARDIANS"
            title="One sanctuary. Three agents."
            description="Hover to expand each product: desktop, tray voice, and mobile."
            cards={NIN_EXPAND_CARDS}
            renderMedia={(card, active) => {
              const kind = NIN_CARD_MEDIA[card.id]
              return kind ? <MarketingCardMedia kind={kind} active={active} /> : null
            }}
          />
        </Ninxl8BitSection>

        <Ninxl8BitDivider />

        <Ninxl8BitSection data-section="content-marquee" className="relative z-20 py-10 md:py-14">
          <MarketingContentMarquee
            label="NIN products"
            tiles={NIN_CONTENT_TILES}
          />
        </Ninxl8BitSection>

        <Ninxl8BitDivider />

        <CmsLandingHighlights />

        <Ninxl8BitDivider />

        {/* PROJECTS */}
        <Ninxl8BitSection id="projects" data-section="projects" className="relative z-20">
          <div className="mx-auto max-w-6xl px-6">
            <NinxlScrollReveal variant="rise" className="mb-10 text-center">
              <div className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">
                OUR PROJECTS
              </div>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-base md:text-lg">
                One platform. Many agents.
              </h2>
              <p className="ninxl-8bit-body mx-auto max-w-2xl">
                Guardians of the sanctuary. NINX on your desktop, NINK for tray voice writing,
                Ningenie on your phone. All keep your data under your control.
              </p>
            </NinxlScrollReveal>
            <NinProductGrid />
          </div>
        </Ninxl8BitSection>

        <Ninxl8BitDivider />

        {/* PRIVACY VAULT. Evervault hover cipher */}
        <Ninxl8BitSection id="privacy" data-section="privacy" className="relative z-20 py-16">
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-10 px-6 md:flex-row md:items-stretch md:justify-between">
            <div className="max-w-md text-center md:text-left">
              <div className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">
                DATA SANCTUARY
              </div>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-base md:text-lg">
                Encrypted at the edge.
              </h2>
              <p className="ninxl-8bit-body">
                Hover the vault to see the cipher field. NIN products keep inference and files on your
                devices unless you choose otherwise.
              </p>
            </div>
            <NinPrivacyVault />
          </div>
        </Ninxl8BitSection>

        <Ninxl8BitDivider />

        {/* CONNECT PLATFORM */}
        <Ninxl8BitSection
          id="connect-platform"
          data-section="connect-platform"
          className="nin-connect-platform-section relative z-20 scroll-mt-24 py-8 md:py-12"
        >
          <div className="mx-auto max-w-6xl px-6">
            <NinConnectPlatformSection variant="org" />
          </div>
        </Ninxl8BitSection>

        <Ninxl8BitDivider />

        {/* CONTACT */}
        <Ninxl8BitSection id="contact" data-section="contact" className="relative z-20 scroll-mt-24 py-16">
          <div className="mx-auto max-w-6xl px-6">
            <NinContactSection variant="org" />
          </div>
        </Ninxl8BitSection>

        <NinOrgFooter />
      </div>
    </div>
  )
})

const NinOrgPage = () => (
  <Ninxl8BitThemeProvider>
    <NinOrgPageContent />
  </Ninxl8BitThemeProvider>
)

export default NinOrgPage
